export type OpenPathsMap = Partial<Record<number, string[]>>;

type OpenPathsValue = string | string[] | undefined;

const normalizePathsAtLevel = (value: unknown): string[] => {
  const raw = value as OpenPathsValue;
  if (Array.isArray(raw)) return raw;
  if (raw === '') return [''];
  return raw ? [raw] : [];
};

const isExplicitlyOpen = ({
  pathsAtLevel,
  fullPath,
  hasItemPath
}: {
  pathsAtLevel: string[];
  fullPath: string;
  hasItemPath: boolean;
}) => {
  return pathsAtLevel.includes(fullPath) || (!hasItemPath && pathsAtLevel.includes(''));
};

const shouldOpenWhenDescendantActive = ({
  level,
  isMenuOpen,
  openPaths,
  isExplicitlyOpenAtLevel
}: {
  level: number;
  isMenuOpen: boolean;
  openPaths: OpenPathsMap;
  isExplicitlyOpenAtLevel: boolean;
}) => {
  if (!isMenuOpen) return true;
  // No explicit choice at this level yet → auto-open the active branch.
  // If the user has explicitly opened something at this level, defer to that choice.
  if (!(level in openPaths)) return true;
  return isExplicitlyOpenAtLevel;
};

export const getShouldBeOpen = ({
  openPaths,
  level,
  isAnyDescendantActive,
  isMenuOpen,
  fullPath,
  hasItemPath
}: {
  openPaths: OpenPathsMap;
  level: number;
  isAnyDescendantActive: boolean;
  isMenuOpen: boolean;
  fullPath: string;
  hasItemPath: boolean;
}) => {
  const pathsAtLevel = normalizePathsAtLevel(openPaths[level]);
  const explicitlyOpen = isExplicitlyOpen({ pathsAtLevel, fullPath, hasItemPath });

  if (isAnyDescendantActive) {
    return shouldOpenWhenDescendantActive({
      level,
      isMenuOpen,
      openPaths,
      isExplicitlyOpenAtLevel: explicitlyOpen
    });
  }

  if (!isMenuOpen) return false;
  if (level === 0 && pathsAtLevel.includes('')) return true;
  return explicitlyOpen;
};

const pruneDeeperLevels = ({
  updated,
  level,
  shouldKeep
}: {
  updated: OpenPathsMap;
  level: number;
  shouldKeep: (path: string) => boolean;
}) => {
  for (const key of Object.keys(updated)) {
    const keyNum = Number(key);
    if (keyNum > level) {
      const filtered = (updated[keyNum] || []).filter(shouldKeep);
      if (filtered.length) updated[keyNum] = filtered;
      else delete updated[keyNum];
    }
  }
};

const closeOpenPath = ({
  updated,
  level,
  fullPath,
  currentAtLevel
}: {
  updated: OpenPathsMap;
  level: number;
  fullPath: string;
  currentAtLevel: string[];
}) => {
  const remaining = currentAtLevel.filter(p => p !== fullPath);
  // Always keep the level key present (even when empty) so !(level in openPaths)
  // stays false and the auto-open logic doesn't re-open a manually closed submenu.
  updated[level] = remaining;

  pruneDeeperLevels({ updated, level, shouldKeep: path => !path.startsWith(fullPath) });
};

const openLevel0 = (updated: OpenPathsMap, fullPath: string) => {
  updated[0] = fullPath ? [fullPath] : [];
};

const openLevel1 = (updated: OpenPathsMap, fullPath: string) => {
  updated[1] = [fullPath];
  pruneDeeperLevels({ updated, level: 1, shouldKeep: path => path.startsWith(fullPath) });
};

const openDeeperLevel = ({
  updated,
  level,
  fullPath,
  currentAtLevel
}: {
  updated: OpenPathsMap;
  level: number;
  fullPath: string;
  currentAtLevel: string[];
}) => {
  updated[level] = [...currentAtLevel, fullPath];
};

export const getNextOpenPaths = (
  prev: OpenPathsMap,
  level: number,
  fullPath: string,
  { isMenuOpen, isCurrentlyOpen }: { isMenuOpen: boolean; isCurrentlyOpen: boolean }
) => {
  const currentAtLevel = normalizePathsAtLevel(prev[level]);
  const updated: OpenPathsMap = { ...prev };

  if (level === 0 && !isMenuOpen) {
    openLevel0(updated, fullPath);
    return updated;
  }

  if (isCurrentlyOpen) {
    closeOpenPath({ updated, level, fullPath, currentAtLevel });
    return updated;
  }

  if (level === 0) {
    openLevel0(updated, fullPath);
    return updated;
  }

  if (level === 1) {
    openLevel1(updated, fullPath);
    return updated;
  }

  openDeeperLevel({ updated, level, fullPath, currentAtLevel });
  return updated;
};

export const getSelectedStyles = (isSelected: boolean, hasChildren: boolean) => {
  if (isSelected) {
    if (hasChildren) {
      return {
        backgroundColor: 'background.paper',
        borderBottom: '2px solid',
        borderColor: 'primary.main'
      };
    }
    return {
      backgroundColor: '#341616 !important'
    };
  }
  return {
    backgroundColor: 'background.paper'
  };
};

export const getSelectedHoverStyles = (isSelected: boolean) => {
  if (isSelected) return {};
  return {
    backgroundColor: 'rgba(255, 255, 255, 0.08)'
  };
};
